import type { BuildQuery, IntrospectionResult } from "ra-data-graphql";
import { buildQuery } from "ra-data-graphql-simple";
import buildDataProvider from "ra-data-graphql-simple";

import { ContentUsageDailyRow, DailyRow, DistributionRow } from "../analytics/utils";
import { LOCATION_LIST_FETCH_LIMIT } from "../constants/pagination";
import { CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE, DEVICE_ENROLLMENT_TOKENS_RESOURCE } from "../constants/resources";
import * as graphql from "../generated/graphql";
import {
  type CompleteOnboardingInput,
  type CompleteOnboardingMutation,
  type CompleteOnboardingMutationVariables,
  CreateUserWithValidationCodeMutation,
  CreateUserWithValidationCodeMutationVariables,
  DataSource,
  GetIntegrationExecutionQuery,
  GetIntegrationExecutionQueryVariables,
  GetIntegrationImportPreviewQuery,
  GetIntegrationImportPreviewQueryVariables,
  ManagerRegistrationCandidateQuery,
  ManagerRegistrationCandidateQueryVariables,
  PasswordResetRecipient,
  PreviewIntegrationImportMutation,
  PreviewIntegrationImportMutationVariables,
  RequestPasswordResetOtpMutation,
  RequestPasswordResetOtpMutationVariables,
  ResetPasswordWithValidationCodeMutation,
  ResetPasswordWithValidationCodeMutationVariables,
  SessionStatus,
  StaffRegistrationCandidateQuery,
  StaffRegistrationCandidateQueryVariables,
  TestIntegrationConnectionMutation,
  TestIntegrationConnectionMutationVariables,
  UpdateImportedPractitionerStatusMutation,
  UpdateImportedPractitionerStatusMutationVariables,
  UserType,
} from "../generated/graphql";
import {
  COMPLETE_ONBOARDING,
  CREATE_USER_WITH_VALIDATION_CODE,
  PREVIEW_INTEGRATION_IMPORT,
  REQUEST_PASSWORD_RESET_OTP,
  RESET_PASSWORD_WITH_VALIDATION_CODE,
  RESET_PRACTITIONER_PASSWORD_OTP,
  REVEAL_PRACTITIONER_PASSWORD_OTP,
  SAVE_INTEGRATION_SETTINGS,
  SYNC_INTEGRATION_DATA,
  TEST_INTEGRATION_CONNECTION,
  UPDATE_IMPORTED_PRACTITIONER_STATUS,
  UPSERT_LOCATION,
} from "../graphql/mutations";
import {
  ANALYTICS_CONTENT_USAGE_DAILY_QUERY,
  ANALYTICS_DAILY_QUERY,
  ANALYTICS_DISTRIBUTION_QUERY,
  DASHBOARD_PATIENT_COUNT,
  DASHBOARD_PATIENT_LIST,
  DASHBOARD_PRACTITIONER_COUNT,
  DASHBOARD_PRACTITIONER_LIST,
  DEVICE_ENROLLMENT_TOKENS,
  GET_INTEGRATION_EXECUTION,
  GET_INTEGRATION_EXECUTIONS,
  GET_INTEGRATION_IMPORT_PREVIEW,
  GET_INTEGRATION_SETTINGS,
  GET_UPLOAD_LINKS_WITH_KEYS,
  LOCATION_OPTIONS_LIST,
  LOCATION_SUMMARY_LIST,
  MANAGER_INVITATIONS,
  MANAGER_REGISTRATION_CANDIDATE,
  PATIENT_ACTIVITIES,
  PRACTITIONER_ACTIVITIES,
  PRACTITIONER_NOTES,
  STAFF_REGISTRATION_CANDIDATE,
} from "../graphql/queries";
import { getAccessStatus } from "../lib/accessStatus";
import { getActivityType, mapActivitySession } from "../lib/activityUtils";
import { client } from "../lib/apolloClient";
import {
  filterCustomerServiceOrganizations,
  toCustomerServiceOrganizationRows,
} from "../lib/customerServiceOrganizations";
import { filterByOrganizations } from "../lib/filterByOrganizations";
import { formatName } from "../lib/formatName";
import { paginate, searchAndPaginate } from "../lib/searchAndPaginate";
import { DeviceRecord } from "../types/device";
import { LocationRecord } from "../types/location";
import { PatientRecord } from "../types/patient";
import { PRACTITIONER_LOCATION_SCOPE, PractitionerRecord } from "../types/practitioner";
import { sortProceduresByPlannedStart } from "./procedureSort";

type AnalyticsDailyQueryRow = NonNullable<graphql.AnalyticsDailyQueryQuery["dailiesList"]>[number];
type AnalyticsDistributionQueryRow = NonNullable<graphql.AnalyticsDistributionQueryQuery["distributionsList"]>[number];
type AnalyticsContentUsageDailyQueryRow = NonNullable<
  graphql.AnalyticsContentUsageDailyQueryQuery["contentUsageDailiesList"]
>[number];

const isOrganizationLocationType = (type?: string | null): boolean => type === graphql.LocationTypeEnum.Organization;

const isOrganizationLocation = (location: { type?: string | null }): boolean =>
  isOrganizationLocationType(location.type);

type LocationHierarchyReference = {
  nodes: Array<{
    locationId?: string | null;
    locationType?: string | null;
  }>;
};

type PatientLocationReference = {
  id?: string | null;
  type?: string | null;
};

const getPhysicalLocationId = (
  location?: PatientLocationReference | null,
  hierarchy?: LocationHierarchyReference | null
): string | undefined => {
  if (!location?.id || isOrganizationLocationType(location.type)) return undefined;

  const assignedLocation = hierarchy?.nodes.find(node => node.locationId === location.id);
  return assignedLocation && isOrganizationLocationType(assignedLocation.locationType) ? undefined : location.id;
};

// Helper to transform location hierarchy from GraphQL response
const transformLocationHierarchy = (
  hierarchy?: {
    nodes: Array<{
      level?: number | null;
      locationId?: string | null;
      locationName?: string | null;
      locationType?: string | null;
      externalId?: string | null;
    }>;
  } | null
) => {
  if (!hierarchy?.nodes) return [];

  return hierarchy.nodes
    .filter(
      (
        node
      ): node is {
        level: number;
        locationId: string;
        locationName: string;
        locationType: string;
        externalId?: string | null;
      } =>
        node.level != null &&
        node.locationId != null &&
        node.locationName != null &&
        node.locationType != null &&
        !isOrganizationLocationType(node.locationType)
    )
    .map(node => ({
      level: node.level,
      locationId: node.locationId,
      locationName: node.locationName,
      locationType: node.locationType,
      externalId: node.externalId ?? undefined,
    }));
};

const toPatientRecord = (
  patient: NonNullable<graphql.PatientsQueryQuery["patients"]>["nodes"][number]
): PatientRecord => ({
  id: patient.id,
  name: formatName(patient.user),
  firstName: patient.user?.firstName,
  lastName: patient.user?.lastName,
  avatarUrl: patient.user?.avatarUrl,
  gender: patient.gender,
  birthDate: patient.birthDate,
  patientNumber: patient.patientNumber,
  organizationName: patient.organization?.name,
  locationId: getPhysicalLocationId(patient.location, patient.locationHierarchy),
  locationHierarchy: transformLocationHierarchy(patient.locationHierarchy),
  sourceSystem: patient.sourceSystem,
});

const toPractitionerRecord = (
  practitioner: NonNullable<graphql.PractitionersQueryQuery["practitioners"]>["nodes"][number]
): PractitionerRecord => {
  const assignedLocations = practitioner.practitioners?.nodes || [];
  return {
    id: practitioner.id,
    name: formatName(practitioner.user) || practitioner.id,
    firstName: practitioner.user?.firstName,
    lastName: practitioner.user?.lastName,
    avatarUrl: practitioner.user?.avatarUrl,
    emailAddress: practitioner.emailAddress,
    employeeNumber: practitioner.employeeNumber,
    organizationName: practitioner.organization?.name,
    locationScope: assignedLocations.some(isOrganizationLocation)
      ? PRACTITIONER_LOCATION_SCOPE.All
      : PRACTITIONER_LOCATION_SCOPE.Selected,
    locations: assignedLocations
      .filter(location => !isOrganizationLocation(location))
      .map(location => ({ id: location.id, name: location.name, type: location.type ?? null })),
    status: practitioner.status,
    sourceSystem: practitioner.sourceSystem,
    userId: practitioner.user?.id,
    authStatus: practitioner.user?.authStatus,
    accessStatus: getAccessStatus(practitioner.status, practitioner.user?.authStatus),
  };
};

const dashboardLocationCondition = (filter: {
  organizationId?: string;
  locationId?: string;
}): graphql.LocationSummaryCondition => ({
  organizationId: filter.organizationId,
  ...(filter.locationId ? { partOfId: filter.locationId } : { type: graphql.LocationTypeEnum.Site }),
});

const customBuildQuery =
  (introspectionResults: IntrospectionResult): BuildQuery =>
  // Using 'any' for params because it has vastly different structures across operations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (fetchType: string, resource: string, params: any) => {
    if (resource === "Location" && fetchType === "GET_LIST" && params.meta?.locationOptions) {
      const condition: graphql.LocationCondition = {
        organizationId: params.filter.organizationId,
      };
      return {
        query: LOCATION_OPTIONS_LIST,
        variables: {
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          condition,
        },
        parseResponse: (response: { data: graphql.LocationOptionsListQuery }) => {
          const locations = response.data.locations || { nodes: [] };
          return {
            data: locations.nodes,
            pageInfo: { hasNextPage: locations.nodes.length === params.pagination.perPage },
          };
        },
      };
    }

    if (resource === "Location" && fetchType === "GET_ONE" && params.meta?.locationOption) {
      const condition: graphql.LocationCondition = {
        id: params.id,
        ...(params.meta.organizationId ? { organizationId: params.meta.organizationId } : {}),
      };
      return {
        query: LOCATION_OPTIONS_LIST,
        variables: { first: 1, offset: 0, condition },
        parseResponse: (response: { data: graphql.LocationOptionsListQuery }) => ({
          data: response.data.locations?.nodes[0] || null,
        }),
      };
    }

    if (resource === "Location" && fetchType === "GET_LIST" && params.meta?.dashboardRows) {
      const condition = dashboardLocationCondition(params.filter);
      return {
        query: LOCATION_SUMMARY_LIST,
        variables: {
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          condition,
        },
        parseResponse: (response: {
          data: {
            locationSummaries?: {
              nodes: Array<{
                id: string;
                organizationId: string;
                partOfId?: string | null;
                name: string;
                type?: graphql.LocationTypeEnum | null;
                patientCount: number;
                practitionerCount: number;
                deviceCount: number;
              }>;
              totalCount: number;
            } | null;
          };
        }) => {
          const locationSummaries = response.data.locationSummaries || { nodes: [], totalCount: 0 };
          return {
            data: locationSummaries.nodes.map(location => ({
              id: location.id,
              name: location.name,
              organizationId: location.organizationId,
              partOfId: location.partOfId,
              patientCount: location.patientCount,
              practitionerCount: location.practitionerCount,
              deviceCount: location.deviceCount,
              type: location.type,
            })),
            total: locationSummaries.totalCount,
          };
        },
      };
    }

    if (resource === "Patient" && fetchType === "GET_LIST" && params.meta?.dashboardRows) {
      return {
        query: DASHBOARD_PATIENT_LIST,
        variables: {
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          targetOrganizationId: params.filter.organizationId,
          selectedLocationId: params.filter.locationId,
        },
        parseResponse: (response: { data: graphql.DashboardPatientListQuery }) => {
          const patients = response.data.dashboardPatients || { nodes: [], totalCount: 0 };
          return {
            data: patients.nodes.map(toPatientRecord),
            total: patients.totalCount,
          };
        },
      };
    }

    if (resource === "Practitioner" && fetchType === "GET_LIST" && params.meta?.dashboardRows) {
      return {
        query: DASHBOARD_PRACTITIONER_LIST,
        variables: {
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          targetOrganizationId: params.filter.organizationId,
          selectedLocationId: params.filter.locationId,
        },
        parseResponse: (response: { data: graphql.DashboardPractitionerListQuery }) => {
          const practitioners = response.data.dashboardPractitioners || { nodes: [], totalCount: 0 };
          return {
            data: practitioners.nodes.map(toPractitionerRecord),
            total: practitioners.totalCount,
          };
        },
      };
    }

    if (fetchType === "GET_LIST" && params.meta?.dashboardCountOnly) {
      if (resource === "Location") {
        return {
          query: LOCATION_SUMMARY_LIST,
          variables: {
            first: 0,
            offset: 0,
            condition: dashboardLocationCondition(params.filter),
          },
          parseResponse: (response: { data: { locationSummaries?: { totalCount: number } | null } }) => ({
            data: [],
            total: response.data.locationSummaries?.totalCount || 0,
          }),
        };
      }
      if (resource === "Patient" || resource === "Practitioner") {
        return {
          query: resource === "Patient" ? DASHBOARD_PATIENT_COUNT : DASHBOARD_PRACTITIONER_COUNT,
          variables: {
            targetOrganizationId: params.filter.organizationId,
            selectedLocationId: params.filter.locationId,
          },
          parseResponse: (response: {
            data: {
              dashboardPatients?: { totalCount: number } | null;
              dashboardPractitioners?: { totalCount: number } | null;
            };
          }) => {
            const connection =
              resource === "Patient" ? response.data.dashboardPatients : response.data.dashboardPractitioners;
            return { data: [], total: connection?.totalCount || 0 };
          },
        };
      }

      if (resource === "Device") {
        const condition: graphql.DeviceCondition = {
          unenrolledAt: null,
          ...(params.filter.organizationId ? { organizationId: params.filter.organizationId } : {}),
          ...(params.filter.locationId ? { locationId: params.filter.locationId } : {}),
        };
        return {
          query: graphql.DevicesQueryDocument,
          variables: { first: 0, offset: 0, condition },
          parseResponse: (response: { data: graphql.DevicesQueryQuery }) => ({
            data: [],
            total: response.data.devices?.totalCount || 0,
          }),
        };
      }
    }

    if (resource === "Organization" && fetchType === "GET_LIST") {
      return {
        query: graphql.OrganizationsQueryDocument,
        variables: {
          first: 1000, // Fetch a large number to enable frontend filtering
          offset: 0,
        },
        parseResponse: (response: { data: graphql.OrganizationsQueryQuery }) => {
          const organizations = response.data.organizations || {
            nodes: [],
            totalCount: 0,
          };

          return {
            data: organizations.nodes,
            total: organizations.totalCount,
          };
        },
      };
    } else if (resource === "Organization" && fetchType === "GET_ONE") {
      return {
        query: graphql.OrganizationQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.OrganizationQueryQuery }) => {
          const organization = response.data.organization;
          if (!organization) return { data: null };

          return {
            data: {
              ...organization,
              practitioners: organization.practitioners?.nodes || [],
              patients: organization.patients?.nodes || [],
              locations: {
                ...organization.locations,
                nodes:
                  organization.locations?.nodes
                    ?.filter(location => !isOrganizationLocation(location))
                    .map(location => ({
                      ...location,
                      patientCount: location.patients?.totalCount || 0,
                      practitionerCount: location.locationPractitioners?.totalCount || 0,
                      deviceCount: location.devices?.totalCount || 0,
                    })) || [],
              },
            },
          };
        },
      };
    } else if (resource === "Organization" && fetchType === "GET_MANY") {
      if (!params.ids) {
        throw new Error("GET_MANY requires params.ids");
      }
      return {
        query: graphql.OrganizationsQueryDocument,
        variables: {
          first: 1000, // adjust as needed for your dataset size
          offset: 0,
        },
        parseResponse: (response: { data: graphql.OrganizationsQueryQuery }) => {
          const organizations = response.data.organizations || {
            nodes: [],
            totalCount: 0,
          };
          const data = (organizations.nodes || []).filter(org => params.ids.includes(org.id));
          return {
            data,
            total: data.length,
          };
        },
      };
    } else if (resource === "Location" && (fetchType === "CREATE" || fetchType === "UPDATE")) {
      // Filter to only include fields that are part of UpsertLocationInput
      // For UPDATE, type might be missing from params.data if it's disabled in the form
      // In that case, use the type from the previous data
      const input = {
        sourceSystem: params.data.sourceSystem || DataSource.M4C,
        type: params.data.type || params.previousData?.type,
        name: params.data.name,
        organizationId: params.data.organizationId,
        externalId: params.data.externalId,
        partOfId: params.data.partOfId,
      };

      return {
        query: UPSERT_LOCATION,
        variables: { input },
        parseResponse: (response: { data: { upsertLocation: { id: string } } }) => ({
          data: response.data.upsertLocation,
        }),
      };
    } else if (resource === "Location" && fetchType === "GET_ONE") {
      return {
        query: graphql.LocationQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.LocationQueryQuery }) => {
          const location = response.data.location;
          if (!location || isOrganizationLocation(location)) {
            return { data: null };
          }
          return {
            data: {
              ...location,
              organizationId: location.organization?.id,
              locationPractitioners: {
                ...location.locationPractitioners,
                nodes: location.locationPractitioners.nodes.map(node => ({
                  ...node,
                  id: node.practitioner?.id,
                  name: formatName(node.practitioner?.user),
                  firstName: node.practitioner?.user?.firstName,
                  lastName: node.practitioner?.user?.lastName,
                  avatarUrl: node.practitioner?.user?.avatarUrl,
                  status: node.practitioner?.status,
                  employeeNumber: node.practitioner?.employeeNumber,
                  emailAddress: node.practitioner?.emailAddress,
                })),
              },
              patients: {
                ...location.patients,
                nodes: location.patients.nodes.map(node => ({
                  ...node,
                  name: node.user?.name,
                  firstName: node.user?.firstName,
                  lastName: node.user?.lastName,
                  avatarUrl: node.user?.avatarUrl,
                })),
              },
            },
          };
        },
      };
    } else if (resource === "Location" && fetchType === "GET_LIST") {
      // Build the condition for database-level filtering
      const condition: graphql.LocationCondition = {};
      if (params.filter.organizationId) {
        condition.organizationId = params.filter.organizationId;
      }
      // Handle type filter - only if it's a single value (PostGraphile doesn't support IN conditions)
      if (params.filter.type && !Array.isArray(params.filter.type)) {
        condition.type = params.filter.type;
      }
      if (params.filter.partOfId) {
        condition.partOfId = params.filter.partOfId;
      }

      return {
        query: graphql.LocationsQueryDocument,
        variables: {
          first: LOCATION_LIST_FETCH_LIMIT,
          offset: 0,
          condition,
        },
        parseResponse: (response: { data: graphql.LocationsQueryQuery }) => {
          const locations = response.data.locations || {
            nodes: [],
            totalCount: 0,
          };

          let filteredLocations = locations.nodes.filter(location => !isOrganizationLocation(location));

          if (params.filter.physicalRootsOnly) {
            const physicalLocationIds = new Set(filteredLocations.map(location => location.id));
            filteredLocations = filteredLocations.filter(
              location => !location.partOfId || !physicalLocationIds.has(location.partOfId)
            );
          }

          // If type is an array, need to filter client-side (PostGraphile limitation)
          if (params.filter.type && Array.isArray(params.filter.type)) {
            filteredLocations = filteredLocations.filter(location => params.filter.type.includes(location.type));
          }

          // Exclude specific location (e.g., to prevent self-reference)
          if (params.filter.excludeId) {
            filteredLocations = filteredLocations.filter(location => location.id !== params.filter.excludeId);
          }

          const data = filteredLocations.map(
            (location): LocationRecord => ({
              id: location.id,
              name: location.name,
              organizationId: location.organization?.id,
              organizationName: location.organization?.name,
              patientCount: location.patients?.totalCount || 0,
              practitionerCount: location.locationPractitioners?.totalCount || 0,
              deviceCount: location.devices?.totalCount || 0,
              externalId: location.externalId,
              type: location.type,
              partOfId: location.partOfId,
              mode: location.mode,
              sourceSystem: location.sourceSystem,
            })
          );

          return {
            ...searchAndPaginate<LocationRecord>(data, params.pagination, params.filter.search, [
              "name",
              "externalId",
              "organizationName",
            ]),
            meta: { isPartial: locations.totalCount > locations.nodes.length },
          };
        },
      };
    } else if (resource === "Location" && fetchType === "GET_MANY") {
      if (!params.ids) {
        throw new Error("GET_MANY requires params.ids");
      }
      return {
        query: graphql.LocationsQueryDocument,
        variables: {
          first: 1000, // adjust as needed for your dataset size
          offset: 0,
        },
        parseResponse: (response: { data: graphql.LocationsQueryQuery }) => {
          const locations = response.data.locations || {
            nodes: [],
            totalCount: 0,
          };

          const data = filterByOrganizations(locations.nodes, params.filter?.organizationId)
            .filter(location => !isOrganizationLocation(location))
            .filter(loc => params.ids.includes(loc.id))
            .map(location => ({
              ...location,
              organizationName: location.organization?.name,
              patientCount: location.patients?.totalCount || 0,
              practitionerCount: location.locationPractitioners?.totalCount || 0,
            }));
          return {
            data,
            total: data.length,
          };
        },
      };
    } else if (resource === "Patient" && fetchType === "GET_LIST") {
      // Build the condition for database-level filtering
      const condition: graphql.PatientCondition = {};
      if (params.filter.organizationId) {
        condition.organizationId = params.filter.organizationId;
      }

      // Check if we need client-side filtering (which requires fetching more data)
      const needsClientSideFiltering = params.filter.locationId || params.filter.search;

      return {
        query: graphql.PatientsQueryDocument,
        variables: {
          // If we need client-side filtering, fetch more records to avoid empty pages
          first: needsClientSideFiltering ? 1000 : params.pagination.perPage,
          offset: needsClientSideFiltering ? 0 : (params.pagination.page - 1) * params.pagination.perPage,
          condition,
        },
        parseResponse: (response: { data: graphql.PatientsQueryQuery }) => {
          const patients = response.data.patients || {
            nodes: [],
            totalCount: 0,
          };

          let filteredPatients = patients.nodes;

          // Filter by location if locationId is provided (client-side for now due to hierarchy complexity)
          if (params.filter.locationId) {
            filteredPatients = filteredPatients.filter(patient => {
              // Check if patient's direct location matches
              if (patient.location?.id === params.filter.locationId) return true;

              // Check if any location in the hierarchy matches
              const hierarchy = transformLocationHierarchy(patient.locationHierarchy);
              return hierarchy.some(loc => loc.locationId === params.filter.locationId);
            });
          }

          const data = filteredPatients.map(toPatientRecord);

          // Handle search and client-side pagination if needed
          if (needsClientSideFiltering) {
            return searchAndPaginate<PatientRecord>(data, params.pagination, params.filter.search, [
              "firstName",
              "lastName",
              "patientNumber",
              "organizationName",
            ]);
          }

          return {
            data,
            total: patients.totalCount,
          };
        },
      };
    } else if (resource === "Patient" && fetchType === "GET_ONE") {
      return {
        query: graphql.PatientQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.PatientQueryQuery }) => {
          const patient = response.data.patient;
          if (!patient) return { data: null };

          return {
            data: {
              ...toPatientRecord(patient),
              userId: patient.user?.id,
              name: patient.user?.name,
              avatarId: patient.user?.avatar,
              email: patient.user?.email,
              salutation: patient.user?.salutation,
              organizationId: patient.organization?.id,
              externalId: patient.externalId,
            },
          };
        },
      };
    } else if (resource === "Patient" && (fetchType === "CREATE" || fetchType === "UPDATE")) {
      // Filter to only include fields that are part of UpsertPatientInput
      const input = {
        sourceSystem: params.data.sourceSystem || DataSource.M4C,
        firstName: params.data.firstName,
        lastName: params.data.lastName,
        salutation: params.data.salutation,
        birthDate: params.data.birthDate,
        patientNumber: params.data.patientNumber,
        organizationId: params.data.organizationId,
        gender: params.data.gender,
        avatarId: params.data.avatarId,
        externalId: params.data.externalId,
        ...(Object.prototype.hasOwnProperty.call(params.data, "locationId")
          ? { locationId: params.data.locationId }
          : {}),
      } as graphql.UpsertPatientInput;

      return {
        query: graphql.UpsertPatientDocument,
        variables: { input },
        parseResponse: (response: { data: graphql.UpsertPatientMutation }) => {
          const patient = response.data.upsertPatient;
          if (!patient) return { data: null };
          return {
            data: {
              id: patient.id,
            },
          };
        },
      };
    } else if (resource === "Practitioner" && fetchType === "GET_ONE") {
      return {
        query: graphql.PractitionerQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.PractitionerQueryQuery }) => {
          const practitioner = response.data.practitioner;
          if (!practitioner) return { data: null };

          return {
            data: {
              ...toPractitionerRecord(practitioner),
              salutation: practitioner.user?.salutation,
              email: practitioner.emailAddress,
              avatarId: practitioner.user?.avatar,
              workerNumber: practitioner.employeeNumber,
              telephone: practitioner.telephoneNumber,
              organizationId: practitioner.organization?.id,
              practitionerId: practitioner?.id,
              externalId: practitioner.externalId,
              gender: practitioner.gender,
              role: practitioner.role,
              motherTongue: practitioner.motherTongue,
              authStatusValidUntil: practitioner.user?.authStatusValidUntil,
            },
          };
        },
      };
    } else if (resource === "PractitionerActivities" && fetchType === "GET_LIST") {
      const sortField = params.sort?.field;
      const sortOrder = params.sort?.order;
      const orderBy = [
        sortField === "beganAt"
          ? sortOrder === "ASC"
            ? graphql.ActivitySessionsOrderBy.BeganAtAsc
            : graphql.ActivitySessionsOrderBy.BeganAtDesc
          : graphql.ActivitySessionsOrderBy.BeganAtDesc, // default fallback
      ];

      return {
        query: PRACTITIONER_ACTIVITIES,
        variables: {
          userId: params.filter.userId,
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          orderBy,
          condition: { sessionStatus: SessionStatus.Completed },
        },
        parseResponse: (response: { data: graphql.PractitionerActivitiesQueryQuery }) => {
          const activities = response.data.user?.activitySessionsByCaregiverId || {
            nodes: [],
            totalCount: 0,
          };

          return {
            data: activities.nodes.map(activity => mapActivitySession(activity)),
            total: activities.totalCount || 0,
          };
        },
      };
    } else if (resource === "PatientActivities" && fetchType === "GET_LIST") {
      const sortField = params.sort?.field;
      const sortOrder = params.sort?.order;
      const orderBy = [
        sortField === "beganAt"
          ? sortOrder === "ASC"
            ? graphql.ActivitySessionsOrderBy.BeganAtAsc
            : graphql.ActivitySessionsOrderBy.BeganAtDesc
          : graphql.ActivitySessionsOrderBy.BeganAtDesc, // default fallback
      ];

      return {
        query: PATIENT_ACTIVITIES,
        variables: {
          userId: params.filter.userId,
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          orderBy,
          condition: { sessionStatus: SessionStatus.Completed },
        },
        parseResponse: (response: { data: graphql.PatientActivitiesQueryQuery }) => {
          const activities = response.data.user?.activitySessionsByActivityParticipantSeniorIdAndActivitySessionId || {
            nodes: [],
            totalCount: 0,
          };

          return {
            data: activities.nodes.map(activity => mapActivitySession(activity)),
            total: activities.totalCount || 0,
          };
        },
      };
    } else if (resource === "PractitionerNotes" && fetchType === "GET_LIST") {
      return {
        query: PRACTITIONER_NOTES,
        variables: {
          userId: params.filter.userId,
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
        },
        parseResponse: (response: { data: graphql.PractitionerNotesQueryQuery }) => {
          const notes = response.data.notes || {
            nodes: [],
            totalCount: 0,
          };

          return {
            data: notes.nodes.map(note => {
              const activitySession = note.activitySessionList?.[0];
              const activityType = activitySession ? getActivityType(activitySession) : undefined;

              return {
                id: note.id,
                note: note.note,
                createdAt: note.createdAt,
                updatedAt: note.updatedAt,
                authorName: formatName(note.author),
                activityType,
              };
            }),
            total: notes.totalCount,
          };
        },
      };
    } else if (resource === "Practitioner" && fetchType === "GET_LIST") {
      // Build the condition for database-level filtering
      const condition: graphql.PractitionerCondition = {};
      if (params.filter.organizationId) {
        condition.organizationId = params.filter.organizationId;
      }

      // Check if we need client-side filtering (which requires fetching more data)
      const needsClientSideFiltering = params.filter.locationId || params.filter.search || params.filter.accessStatus;

      return {
        query: graphql.PractitionersQueryDocument,
        variables: {
          // If we need client-side filtering, fetch more records to avoid empty pages
          first: needsClientSideFiltering ? 1000 : params.pagination.perPage,
          offset: needsClientSideFiltering ? 0 : (params.pagination.page - 1) * params.pagination.perPage,
          condition,
        },
        parseResponse: (response: { data: graphql.PractitionersQueryQuery }) => {
          const practitioners = response.data.practitioners || {
            nodes: [],
            totalCount: 0,
          };

          let filteredPractitioners = practitioners.nodes;

          // Filter by location if locationId is provided (client-side for now)
          if (params.filter.locationId) {
            filteredPractitioners = filteredPractitioners.filter(practitioner =>
              practitioner.practitioners?.nodes?.some(
                loc => loc.id === params.filter.locationId || loc.partOfId === params.filter.locationId
              )
            );
          }

          const data = filteredPractitioners.map(toPractitionerRecord);

          const filteredData = params.filter.accessStatus
            ? data.filter(record => record.accessStatus === params.filter.accessStatus)
            : data;

          // Handle search and client-side pagination if needed
          if (needsClientSideFiltering) {
            return searchAndPaginate<PractitionerRecord>(filteredData, params.pagination, params.filter.search, [
              "firstName",
              "lastName",
              "name",
              "emailAddress",
              "employeeNumber",
              "organizationName",
            ]);
          }

          return {
            data,
            total: practitioners.totalCount,
          };
        },
      };
    } else if (resource === "Practitioner" && (fetchType === "CREATE" || fetchType === "UPDATE")) {
      // Filter to only include fields that are part of UpsertPractitionerInput
      const locationIds = Array.isArray(params.data.locationIds) ? { locationIds: params.data.locationIds } : {};

      const input: graphql.UpsertPractitionerInput = {
        sourceSystem: params.data.sourceSystem || DataSource.M4C,
        firstName: params.data.firstName,
        lastName: params.data.lastName,
        organizationId: params.data.organizationId,
        ...locationIds,
        externalId: params.data.externalId,
        ...(Object.prototype.hasOwnProperty.call(params.data, "workerNumber")
          ? { workerNumber: params.data.workerNumber }
          : {}),
        email: params.data.email,
        telephone: params.data.telephone,
        status: params.data.status,
        salutation: params.data.salutation,
        gender: params.data.gender,
        avatarId: params.data.avatarId,
        role: params.data.role,
        motherTongue: params.data.motherTongue,
      };

      return {
        query: graphql.UpsertPractitionerDocument,
        variables: { input },
        parseResponse: (response: { data: graphql.UpsertPractitionerMutation }) => {
          const practitioner = response.data.upsertPractitioner;
          if (!practitioner) return { data: null };
          return {
            data: {
              id: practitioner.id,
              warningCode: practitioner.warningCode,
            },
          };
        },
      };
    } else if (resource === "Device" && fetchType === "GET_LIST") {
      // Build the condition for database-level filtering
      const condition: graphql.DeviceCondition = {};
      if (params.filter.organizationId) {
        condition.organizationId = params.filter.organizationId;
      }
      if (params.filter.locationId) {
        condition.locationId = params.filter.locationId;
      }
      if (
        params.filter.enrollmentStatus === graphql.DeviceEnrollmentStatus.Active ||
        params.filter.enrollmentStatus === graphql.DeviceEnrollmentStatus.Inactive ||
        params.filter.enrollmentStatus === graphql.DeviceEnrollmentStatus.Unenrolled
      ) {
        condition.enrollmentStatus = params.filter.enrollmentStatus;
      }

      return {
        query: graphql.DevicesQueryDocument,
        variables: {
          first: params.pagination.perPage,
          offset: (params.pagination.page - 1) * params.pagination.perPage,
          condition,
        },
        parseResponse: (response: { data: graphql.DevicesQueryQuery }) => {
          const devices = response.data.devices || {
            nodes: [],
            totalCount: 0,
          };

          const data = devices.nodes;

          // Handle search client-side, but use actual totalCount from GraphQL response
          if (params.filter.search) {
            const searchFields = ["name", "deviceId", "status", "manufacturer", "model", "osVersion", "appVersion"];
            const searchLower = params.filter.search.toLowerCase();
            const searchFiltered = data.filter(item =>
              searchFields.some(field => {
                const value = item[field as keyof DeviceRecord];
                return value && String(value).toLowerCase().includes(searchLower);
              })
            );
            return {
              data: searchFiltered,
              total: searchFiltered.length,
            };
          }

          return {
            data,
            total: devices.totalCount,
          };
        },
      };
    } else if (resource === "Device" && fetchType === "GET_ONE") {
      return {
        query: graphql.DeviceQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.DeviceQueryQuery }) => {
          const device = response.data.device;
          if (!device) return { data: null };
          return {
            data: device,
          };
        },
      };
    } else if (resource === CUSTOMER_SERVICE_ORGANIZATIONS_RESOURCE && fetchType === "GET_LIST") {
      return {
        query: graphql.CustomerServiceOrganizationAccountsDocument,
        variables: {},
        parseResponse: (response: { data: graphql.CustomerServiceOrganizationAccountsQuery }) =>
          filterCustomerServiceOrganizations(
            toCustomerServiceOrganizationRows(response.data.customerServiceOrganizationAccounts),
            { search: params.filter?.search, unlinked: params.filter?.unlinked === true },
            params.pagination
          ),
      };
    } else if (resource === "Invitation" && fetchType === "GET_LIST") {
      return {
        query: MANAGER_INVITATIONS,
        variables: {},
        parseResponse: (response: { data: { managerInvitations: Array<graphql.ManagerInvitation> } }) => {
          return searchAndPaginate<graphql.ManagerInvitation>(
            response.data.managerInvitations || [],
            params.pagination,
            params.filter?.search,
            ["email", "salesforceAccountName"]
          );
        },
      };
    } else if (resource === "Procedure" && fetchType === "GET_LIST") {
      // Build the condition for database-level filtering
      const condition: graphql.ProcedureCondition = {};
      if (params.filter?.organizationId) {
        condition.organizationId = params.filter.organizationId;
      }
      if (params.filter?.status) {
        condition.status = params.filter.status;
      }
      if (params.filter?.sourceSystem) {
        condition.sourceSystem = params.filter.sourceSystem;
      }
      if (params.filter?.["patient.id"]) {
        condition.patientId = params.filter["patient.id"];
      }

      // Handle sorting
      const sortField = params.sort?.field;
      const sortOrder = params.sort?.order;
      const orderBy = [
        sortField === "plannedStart" && sortOrder === "ASC"
          ? graphql.ProceduresOrderBy.PlannedStartAscNullsLast
          : graphql.ProceduresOrderBy.PlannedStartDescNullsLast, // default: latest planned first
      ];

      // The location filter goes through the patient's location, so it is applied client-side
      const needsClientSideFiltering = !!params.filter?.locationId;

      return {
        query: graphql.ProceduresQueryDocument,
        variables: {
          first: needsClientSideFiltering ? 1000 : params.pagination.perPage,
          offset: needsClientSideFiltering ? 0 : (params.pagination.page - 1) * params.pagination.perPage,
          condition,
          orderBy,
        },
        parseResponse: (response: { data: graphql.ProceduresQueryQuery }) => {
          const procedures = response.data.procedures || {
            nodes: [],
            totalCount: 0,
          };

          let filteredProcedures = procedures.nodes;

          // Filter by location if locationId is provided (client-side filtering via patient location)
          if (params.filter?.locationId) {
            filteredProcedures = filteredProcedures.filter(procedure =>
              procedure.procedureParticipants?.nodes?.some(
                participant =>
                  participant.patient?.location?.id === params.filter.locationId ||
                  participant.patient?.location?.partOfId === params.filter.locationId
              )
            );
          }

          // Apply client-side pagination when we did client-side filtering
          if (needsClientSideFiltering) {
            // The location path fetches a wide window; re-sort it the same way the server orders pages.
            if (!sortField || sortField === "plannedStart") {
              filteredProcedures = sortProceduresByPlannedStart(filteredProcedures, sortOrder === "ASC");
            }
            return paginate(filteredProcedures, params.pagination);
          }

          return {
            data: filteredProcedures,
            total: procedures.totalCount,
          };
        },
      };
    } else if (resource === DEVICE_ENROLLMENT_TOKENS_RESOURCE && fetchType === "GET_LIST") {
      const organizationId = params.filter?.organizationId;
      if (!organizationId) {
        return {
          query: DEVICE_ENROLLMENT_TOKENS,
          variables: {},
          parseResponse: () => ({ data: [], total: 0 }),
        };
      }

      return {
        query: DEVICE_ENROLLMENT_TOKENS,
        variables: { organizationId },
        parseResponse: (response: { data: graphql.DeviceEnrollmentTokensQuery }) => {
          const tokens = response.data.deviceEnrollmentTokens || [];
          return paginate(
            tokens.filter(token => !token.implicit),
            params.pagination
          );
        },
      };
    } else if (resource === "Procedure" && fetchType === "GET_ONE") {
      return {
        query: graphql.ProcedureQueryDocument,
        variables: { id: params.id },
        parseResponse: (response: { data: graphql.ProcedureQueryQuery }) => {
          const procedure = response.data.procedure;
          if (!procedure) return { data: null };
          return {
            data: procedure,
          };
        },
      };
    }
    return buildQuery(introspectionResults)(fetchType, resource, params);
  };

const baseDataProvider = buildDataProvider({
  client,
  buildQuery: customBuildQuery,
});

type PractitionerCustomParams = {
  practitionerId?: string;
  userId?: string;
  recipient?: PasswordResetRecipient;
  password?: string;
  status?: graphql.PractitionerStatus;
};

const customDataProvider = {
  ...baseDataProvider,
  // FIXME: NG-7855 This name is so generic, we should rename it to something more specific
  custom: async (resource: string, type: string, params: PractitionerCustomParams) => {
    if (resource === "Practitioner" && type === "resetPasswordOTP") {
      const result = await client.mutate({
        mutation: RESET_PRACTITIONER_PASSWORD_OTP,
        variables: {
          input: {
            userId: params.userId,
            recipient: params.recipient,
          },
        },
      });
      return result.data.resetPractitionerPasswordOTP;
    }
    if (resource === "Practitioner" && type === "revealPasswordOTP") {
      const result = await client.mutate({
        mutation: REVEAL_PRACTITIONER_PASSWORD_OTP,
        variables: {
          input: {
            userId: params.userId,
            password: params.password,
          },
        },
      });
      return result.data.revealPractitionerPasswordOTP;
    }
    if (resource === "Practitioner" && type === "updateImportedStatus") {
      if (!params.practitionerId || !params.status) {
        throw new Error("Imported practitioner status requires an id and status");
      }
      const result = await client.mutate<
        UpdateImportedPractitionerStatusMutation,
        UpdateImportedPractitionerStatusMutationVariables
      >({
        mutation: UPDATE_IMPORTED_PRACTITIONER_STATUS,
        variables: {
          input: {
            id: params.practitionerId,
            status: params.status,
          },
        },
      });
      const response = result.data?.updateImportedPractitionerStatus;
      if (!response) {
        throw new Error("Imported practitioner status update returned no data");
      }
      return response;
    }
    throw new Error(`Unknown custom action ${type} on resource ${resource}`);
  },
  registerUser: async (registrationData: {
    email: string;
    name: string;
    password: string;
    code: string;
    kind: UserType.Manager | UserType.CustomerService;
    organizationName?: string;
  }) => {
    const result = await client.mutate<
      CreateUserWithValidationCodeMutation,
      CreateUserWithValidationCodeMutationVariables
    >({
      mutation: CREATE_USER_WITH_VALIDATION_CODE,
      variables: {
        ...registrationData,
        organizationName: registrationData.organizationName ?? null,
      },
    });
    return result.data?.createUserWithValidationCode;
  },
  getManagerRegistrationCandidate: async (params: { email: string; code: string }) => {
    const result = await client.query<ManagerRegistrationCandidateQuery, ManagerRegistrationCandidateQueryVariables>({
      query: MANAGER_REGISTRATION_CANDIDATE,
      variables: {
        email: params.email,
        code: params.code,
      },
      fetchPolicy: "network-only", // A cached result could leak another email's candidate
    });
    return result.data.managerRegistrationCandidate;
  },
  getStaffRegistrationCandidate: async (params: { email: string; code: string }) => {
    const result = await client.query<StaffRegistrationCandidateQuery, StaffRegistrationCandidateQueryVariables>({
      query: STAFF_REGISTRATION_CANDIDATE,
      variables: {
        email: params.email,
        code: params.code,
      },
      fetchPolicy: "network-only", // A cached result could leak another email's candidate
    });
    return result.data.staffRegistrationCandidate ?? null;
  },
  requestPasswordResetOTP: async (email: string): Promise<boolean> => {
    const result = await client.mutate<RequestPasswordResetOtpMutation, RequestPasswordResetOtpMutationVariables>({
      mutation: REQUEST_PASSWORD_RESET_OTP,
      variables: { email },
    });

    if (result.data?.requestPasswordResetOTP === undefined) {
      throw new Error("Failed to request password reset OTP");
    }

    return result.data.requestPasswordResetOTP;
  },
  resetPasswordWithValidationCode: async (data: {
    code: string;
    email: string;
    password: string;
  }): Promise<{ jwtToken: string | null }> => {
    const result = await client.mutate<
      ResetPasswordWithValidationCodeMutation,
      ResetPasswordWithValidationCodeMutationVariables
    >({
      mutation: RESET_PASSWORD_WITH_VALIDATION_CODE,
      variables: {
        code: data.code,
        email: data.email,
        password: data.password,
      },
    });

    if (!result.data?.resetPasswordWithValidationCode) {
      throw new Error("Failed to reset password with validation code");
    }

    return { jwtToken: result.data.resetPasswordWithValidationCode.jwtToken ?? null };
  },
  getUploadLink: async (key: string) => {
    const result = await client.query({
      query: GET_UPLOAD_LINKS_WITH_KEYS,
      variables: {
        key,
      },
    });
    return result.data.getUploadLinksWithKeys[0];
  },

  // Integration settings operations
  getIntegrationSettings: async (organizationId: string) => {
    const result = await client.query({
      query: GET_INTEGRATION_SETTINGS,
      variables: { organizationId },
      fetchPolicy: "network-only",
    });
    return result.data.getIntegrationSettings;
  },

  saveIntegrationSettings: async (organizationId: string, provider: string, credentials: Record<string, string>) => {
    const result = await client.mutate({
      mutation: SAVE_INTEGRATION_SETTINGS,
      variables: {
        organizationId,
        provider,
        credentials,
      },
    });
    return result.data.saveIntegrationSettings;
  },
  syncIntegrationData: async (organizationId: string) => {
    const result = await client.mutate({
      mutation: SYNC_INTEGRATION_DATA,
      variables: { organizationId },
    });
    return result.data.syncIntegrationData;
  },
  testIntegrationConnection: async (organizationId: string) => {
    const result = await client.mutate<TestIntegrationConnectionMutation, TestIntegrationConnectionMutationVariables>({
      mutation: TEST_INTEGRATION_CONNECTION,
      variables: { organizationId },
    });
    if (!result.data?.testIntegrationConnection) {
      throw new Error("Failed to test integration connection");
    }
    return result.data.testIntegrationConnection;
  },
  previewIntegrationImport: async (organizationId: string) => {
    const result = await client.mutate<PreviewIntegrationImportMutation, PreviewIntegrationImportMutationVariables>({
      mutation: PREVIEW_INTEGRATION_IMPORT,
      variables: { organizationId },
    });
    if (!result.data?.previewIntegrationImport) {
      throw new Error("Failed to start integration import preview");
    }
    return result.data.previewIntegrationImport;
  },
  getIntegrationExecution: async (organizationId: string, id: string) => {
    const result = await client.query<GetIntegrationExecutionQuery, GetIntegrationExecutionQueryVariables>({
      query: GET_INTEGRATION_EXECUTION,
      variables: { organizationId, id },
      fetchPolicy: "network-only",
    });
    if (!result.data.integrationExecution) {
      throw new Error("Integration execution not found");
    }
    return result.data.integrationExecution;
  },
  getIntegrationImportPreview: async (organizationId: string) => {
    const result = await client.query<GetIntegrationImportPreviewQuery, GetIntegrationImportPreviewQueryVariables>({
      query: GET_INTEGRATION_IMPORT_PREVIEW,
      variables: { organizationId },
      fetchPolicy: "network-only",
    });
    return result.data.integrationImportPreview ?? null;
  },

  completeOnboarding: async (input: CompleteOnboardingInput) => {
    const result = await client.mutate<CompleteOnboardingMutation, CompleteOnboardingMutationVariables>({
      mutation: COMPLETE_ONBOARDING,
      variables: { input },
    });
    if (!result.data?.completeOnboarding) {
      throw new Error("Failed to complete onboarding");
    }
    return result.data.completeOnboarding;
  },

  // Integration execution operations
  getIntegrationExecutions: async (organizationId: string, input?: { limit?: number; offset?: number }) => {
    const result = await client.query({
      query: GET_INTEGRATION_EXECUTIONS,
      variables: { organizationId, input },
      fetchPolicy: "network-only", // Always fetch fresh data for executions
    });
    return result.data.integrationExecutions;
  },

  getAnalyticsDailies: async (): Promise<DailyRow[]> => {
    const result = await client.query({
      query: ANALYTICS_DAILY_QUERY,
      fetchPolicy: "network-only",
    });
    return (result.data?.dailiesList ?? []).filter((row: AnalyticsDailyQueryRow): row is DailyRow => row != null);
  },

  getAnalyticsDistributions: async (): Promise<DistributionRow[]> => {
    const result = await client.query({
      query: ANALYTICS_DISTRIBUTION_QUERY,
      fetchPolicy: "network-only",
    });
    return (result.data?.distributionsList ?? []).filter(
      (row: AnalyticsDistributionQueryRow): row is DistributionRow => row != null
    );
  },

  getAnalyticsContentUsageDaily: async (): Promise<ContentUsageDailyRow[]> => {
    const result = await client.query({
      query: ANALYTICS_CONTENT_USAGE_DAILY_QUERY,
      fetchPolicy: "network-only",
    });
    return (result.data?.contentUsageDailiesList ?? []).filter(
      (row: AnalyticsContentUsageDailyQueryRow): row is ContentUsageDailyRow => row != null
    );
  },
};

export type CustomDataProvider = typeof customDataProvider;
export default customDataProvider;
